import React, {
    useEffect,
    useMemo,
    useState,
} from "react";

import axios from "axios";


/* ============================================================
   API ENDPOINTS

   IMPORTANT:
   Keep/use your existing Azure Function keys here.

   We are deliberately leaving the Function-key security
   architecture alone for this Admin release. That can be
   addressed properly during the OPPIES 2.2 security work.
   ============================================================ */

const GET_ALL_SALES_API =
    "https://oppiesfunc.azurewebsites.net/api/getAllSales?code=oFkO4JAQQtEYs7dYsEjdbDo1YL-K2Zk2li4PV_kwYmb_AzFuEzmNOQ==";

const GET_ALL_SHOPS_API =
    "https://oppiesfunc.azurewebsites.net/api/getAllShops?code=Dx_kliFsd4-5j40_pt5i6RsR36dv01K92JHMPb6eQRHBAzFuQKNV3A==";

const CREATE_SALE_API =
    "https://oppiesfunc.azurewebsites.net/api/createSalesEventAdmin?code=8Itd61K6iGF-G_WLVtLAWjTXwIDyoEEutG5cNXl58vK7AzFuIzD0lQ==";

const DELETE_SALE_API =
    "https://oppiesfunc.azurewebsites.net/api/deleteSalesEvent?code=ae0byJP2mgxW18u5Uu2uL8PfJD-5TKRbbHJxpoLiRk28AzFuiE1cBg==";


/* ============================================================
   HELPERS
   ============================================================ */

const normalize = (value) =>
    String(value || "")
        .trim()
        .toLowerCase();


const formatDate = (value) => {
    if (!value) {
        return "Not specified";
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return date.toLocaleDateString("en-NZ", {
        day: "numeric",
        month: "long",
        year: "numeric",
    });
};


const formatDateTime = (value) => {
    if (!value) {
        return "Not specified";
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return date.toLocaleString("en-NZ", {
        day: "numeric",
        month: "long",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
    });
};


const formatShortDate = (value) => {
    if (!value) {
        return "";
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return date.toLocaleDateString("en-NZ", {
        day: "numeric",
        month: "short",
        year: "numeric",
    });
};


const getSaleStatus = (sale) => {
    const now = new Date();

    const start = sale?.startDate
        ? new Date(sale.startDate)
        : null;

    const end = sale?.endDate
        ? new Date(sale.endDate)
        : null;


    if (
        start &&
        !Number.isNaN(start.getTime()) &&
        start > now
    ) {
        return "upcoming";
    }


    if (
        end &&
        !Number.isNaN(end.getTime()) &&
        end < now
    ) {
        return "ended";
    }


    return "active";
};


const getStatusLabel = (status) => {
    switch (status) {
        case "active":
            return "ACTIVE";

        case "upcoming":
            return "UPCOMING";

        case "ended":
            return "ENDED";

        default:
            return "";
    }
};


const toDateTimeLocal = (date) => {
    const d = date instanceof Date
        ? date
        : new Date(date);

    if (Number.isNaN(d.getTime())) {
        return "";
    }

    const pad = (value) =>
        String(value).padStart(2, "0");

    return (
        `${d.getFullYear()}-` +
        `${pad(d.getMonth() + 1)}-` +
        `${pad(d.getDate())}T` +
        `${pad(d.getHours())}:` +
        `${pad(d.getMinutes())}`
    );
};


const getDefaultStartDate = () => {
    const now = new Date();

    now.setSeconds(0);
    now.setMilliseconds(0);

    return toDateTimeLocal(now);
};


const getDefaultEndDate = () => {
    const date = new Date();

    date.setDate(date.getDate() + 7);
    date.setHours(23, 59, 0, 0);

    return toDateTimeLocal(date);
};


const getAdminEmail = () => {
    /*
     * Different Admin Portal builds may store the logged-in
     * administrator under different keys.
     *
     * Try the common values first. If none exist, the Function
     * will record "OPPIES Admin".
     */

    try {
        const possibleKeys = [
            "adminEmail",
            "userEmail",
            "email",
        ];

        for (const key of possibleKeys) {
            const value =
                localStorage.getItem(key);

            if (value) {
                return value;
            }
        }


        const user =
            localStorage.getItem("user");

        if (user) {
            const parsed =
                JSON.parse(user);

            if (parsed?.email) {
                return parsed.email;
            }
        }

    } catch (error) {
        console.warn(
            "Could not determine Admin email:",
            error
        );
    }

    return "OPPIES Admin";
};


/* ============================================================
   COMPONENT
   ============================================================ */

export default function SalesManager() {

    /* ----------------------------------------------------------
       DATA
       ---------------------------------------------------------- */

    const [sales, setSales] =
        useState([]);

    const [shops, setShops] =
        useState([]);

    const [loading, setLoading] =
        useState(true);

    const [error, setError] =
        useState("");


    /* ----------------------------------------------------------
       SEARCH / FILTER
       ---------------------------------------------------------- */

    const [searchTerm, setSearchTerm] =
        useState("");

    const [showSearchSuggestions, setShowSearchSuggestions] =
        useState(false);

    const [selectedShopFilter, setSelectedShopFilter] =
        useState(null);

    const [statusFilter, setStatusFilter] =
        useState("all");


    /* ----------------------------------------------------------
       SALE DETAILS MODAL
       ---------------------------------------------------------- */

    const [selectedSale, setSelectedSale] =
        useState(null);


    /* ----------------------------------------------------------
       DELETE MODAL
       ---------------------------------------------------------- */

    const [showDeleteConfirm, setShowDeleteConfirm] =
        useState(false);

    const [deleting, setDeleting] =
        useState(false);

    const [deleteError, setDeleteError] =
        useState("");


    /* ----------------------------------------------------------
       CREATE MODAL
       ---------------------------------------------------------- */

    const [showCreateModal, setShowCreateModal] =
        useState(false);

    const [creating, setCreating] =
        useState(false);

    const [createError, setCreateError] =
        useState("");

    const [createSuccess, setCreateSuccess] =
        useState("");


    /* ----------------------------------------------------------
       CREATE - SHOP SEARCH
       ---------------------------------------------------------- */

    const [shopSearch, setShopSearch] =
        useState("");

    const [showShopSuggestions, setShowShopSuggestions] =
        useState(false);

    const [createShop, setCreateShop] =
        useState(null);


    /* ----------------------------------------------------------
       CREATE FORM
       ---------------------------------------------------------- */

    const [saleTitle, setSaleTitle] =
        useState("");

    const [saleDescription, setSaleDescription] =
        useState("");

    const [saleStartDate, setSaleStartDate] =
        useState(getDefaultStartDate());

    const [saleEndDate, setSaleEndDate] =
        useState(getDefaultEndDate());

    const [saleImageURL, setSaleImageURL] =
        useState("");


    /* ==========================================================
       LOAD DATA
       ========================================================== */

    useEffect(() => {
        loadData();
    }, []);


    const loadData = async () => {
        setLoading(true);
        setError("");

        try {

            const [
                salesResponse,
                shopsResponse,
            ] = await Promise.all([
                axios.get(GET_ALL_SALES_API),
                axios.get(GET_ALL_SHOPS_API),
            ]);


            /*
             * getAllSales currently returns:
             *
             * {
             *   sales: [...],
             *   count: ...
             * }
             *
             * But accepting a raw array as well makes the Admin page
             * a little more resilient.
             */

            const salesData =
                Array.isArray(salesResponse.data)
                    ? salesResponse.data
                    : salesResponse.data?.sales || [];


            /*
             * Same idea for getAllShops.
             */

            const shopsData =
                Array.isArray(shopsResponse.data)
                    ? shopsResponse.data
                    : shopsResponse.data?.shops ||
                    shopsResponse.data?.data ||
                    [];


            setShops(shopsData);


            /*
             * getAllSales should already enrich each record with
             * shopName and shopAddress.
             *
             * This fallback lookup means the UI will still work if
             * an older Function response doesn't contain them.
             */

            const shopLookup =
                new Map();

            shopsData.forEach((shop) => {
                if (!shop?.shopId) {
                    return;
                }

                shopLookup.set(
                    normalize(shop.shopId),
                    shop
                );
            });


            const enrichedSales =
                salesData.map((sale) => {

                    const shop =
                        shopLookup.get(
                            normalize(sale.shopId)
                        );


                    return {
                        ...sale,

                        shopName:
                            sale.shopName ||
                            shop?.name ||
                            sale.shopId ||
                            "Unknown Op Shop",

                        shopAddress:
                            sale.shopAddress ||
                            shop?.address ||
                            "Address not available",
                    };
                });


            /*
             * Newest CREATED first.
             */

            enrichedSales.sort((a, b) => {

                const aTime =
                    new Date(
                        a.createdAt || 0
                    ).getTime();

                const bTime =
                    new Date(
                        b.createdAt || 0
                    ).getTime();

                return bTime - aTime;
            });


            setSales(enrichedSales);

        } catch (err) {

            console.error(
                "Failed to load sales:",
                err
            );

            setError(
                "Unable to load sales events. Please try again."
            );

        } finally {
            setLoading(false);
        }
    };


    /* ==========================================================
       COUNTS
       ========================================================== */

    const saleCounts =
        useMemo(() => {

            const counts = {
                all: sales.length,
                active: 0,
                upcoming: 0,
                ended: 0,
            };


            sales.forEach((sale) => {

                const status =
                    getSaleStatus(sale);

                if (
                    Object.prototype.hasOwnProperty.call(
                        counts,
                        status
                    )
                ) {
                    counts[status] += 1;
                }
            });


            return counts;

        }, [sales]);


    /* ==========================================================
       MAIN SEARCH
       ========================================================== */

    const filteredSales =
        useMemo(() => {

            const query =
                normalize(searchTerm);


            return sales.filter((sale) => {

                /*
                 * STATUS FILTER
                 */

                if (
                    statusFilter !== "all" &&
                    getSaleStatus(sale) !== statusFilter
                ) {
                    return false;
                }


                /*
                 * SELECTED SHOP FILTER
                 */

                if (
                    selectedShopFilter &&
                    normalize(sale.shopId) !==
                    normalize(
                        selectedShopFilter.shopId
                    )
                ) {
                    return false;
                }


                /*
                 * TEXT SEARCH
                 */

                if (!query) {
                    return true;
                }


                const searchableText = [
                    sale.title,
                    sale.description,
                    sale.shopName,
                    sale.shopAddress,
                    sale.shopId,
                    sale.createdBy,
                ]
                    .map(normalize)
                    .join(" ");


                return searchableText.includes(
                    query
                );
            });

        }, [
            sales,
            searchTerm,
            statusFilter,
            selectedShopFilter,
        ]);


    /* ==========================================================
       SEARCH AUTOCOMPLETE
  
       Returns BOTH shops and individual sales.
       ========================================================== */

    const searchSuggestions =
        useMemo(() => {

            const query =
                normalize(searchTerm);

            if (
                !query ||
                query.length < 2
            ) {
                return [];
            }


            /*
             * SHOP MATCHES
             */

            const shopMatches =
                shops
                    .filter((shop) => {

                        const text = [
                            shop.name,
                            shop.address,
                            shop.shopId,
                        ]
                            .map(normalize)
                            .join(" ");

                        return text.includes(query);
                    })
                    .slice(0, 5)
                    .map((shop) => ({
                        type: "shop",
                        shop,
                    }));


            /*
             * SALE MATCHES
             */

            const saleMatches =
                sales
                    .filter((sale) => {

                        const text = [
                            sale.title,
                            sale.description,
                            sale.shopName,
                            sale.shopAddress,
                            sale.createdBy,
                        ]
                            .map(normalize)
                            .join(" ");

                        return text.includes(query);
                    })
                    .slice(0, 5)
                    .map((sale) => ({
                        type: "sale",
                        sale,
                    }));


            return [
                ...shopMatches,
                ...saleMatches,
            ].slice(0, 10);

        }, [
            searchTerm,
            shops,
            sales,
        ]);


    const selectSearchSuggestion =
        (suggestion) => {

            if (
                suggestion.type === "shop"
            ) {

                const shop =
                    suggestion.shop;

                setSelectedShopFilter(shop);

                setSearchTerm(
                    shop.name ||
                    shop.shopId ||
                    ""
                );

                setShowSearchSuggestions(false);

                return;
            }


            if (
                suggestion.type === "sale"
            ) {

                const sale =
                    suggestion.sale;

                setSelectedShopFilter(null);

                setSearchTerm(
                    sale.title || ""
                );

                setShowSearchSuggestions(false);

                setSelectedSale(sale);
            }
        };


    const clearSearch = () => {
        setSearchTerm("");
        setSelectedShopFilter(null);
        setShowSearchSuggestions(false);
    };


    /* ==========================================================
       CREATE SHOP AUTOCOMPLETE
       ========================================================== */

    const createShopSuggestions =
        useMemo(() => {

            const query =
                normalize(shopSearch);

            if (
                !query ||
                query.length < 2
            ) {
                return [];
            }


            return shops
                .filter((shop) => {

                    const searchable = [
                        shop.name,
                        shop.address,
                        shop.shopId,
                    ]
                        .map(normalize)
                        .join(" ");

                    return searchable.includes(
                        query
                    );
                })
                .slice(0, 8);

        }, [
            shops,
            shopSearch,
        ]);


    const selectCreateShop =
        (shop) => {

            setCreateShop(shop);

            setShopSearch(
                shop.name ||
                shop.shopId ||
                ""
            );

            setShowShopSuggestions(false);
        };


    /* ==========================================================
       CREATE MODAL
       ========================================================== */

    const openCreateModal = () => {

        setCreateShop(null);
        setShopSearch("");

        setSaleTitle("");
        setSaleDescription("");

        setSaleStartDate(
            getDefaultStartDate()
        );

        setSaleEndDate(
            getDefaultEndDate()
        );

        setSaleImageURL("");

        setCreateError("");
        setCreateSuccess("");

        setShowCreateModal(true);
    };


    const closeCreateModal = () => {

        if (creating) {
            return;
        }

        setShowCreateModal(false);
        setShowShopSuggestions(false);
        setCreateError("");
        setCreateSuccess("");
    };


    /* ==========================================================
       CREATE SALE
       ========================================================== */

    const handleCreateSale =
        async () => {

            setCreateError("");
            setCreateSuccess("");


            if (!createShop?.shopId) {

                setCreateError(
                    "Please select an Op Shop."
                );

                return;
            }


            if (!saleTitle.trim()) {

                setCreateError(
                    "Please enter a sale title."
                );

                return;
            }


            if (!saleStartDate) {

                setCreateError(
                    "Please select a start date."
                );

                return;
            }


            if (!saleEndDate) {

                setCreateError(
                    "Please select an end date."
                );

                return;
            }


            const start =
                new Date(saleStartDate);

            const end =
                new Date(saleEndDate);


            if (
                Number.isNaN(start.getTime()) ||
                Number.isNaN(end.getTime())
            ) {

                setCreateError(
                    "Please enter valid start and end dates."
                );

                return;
            }


            if (end < start) {

                setCreateError(
                    "The end date cannot be before the start date."
                );

                return;
            }


            setCreating(true);


            try {

                const payload = {
                    shopId:
                        createShop.shopId,

                    title:
                        saleTitle.trim(),

                    description:
                        saleDescription.trim(),

                    startDate:
                        start.toISOString(),

                    endDate:
                        end.toISOString(),

                    imageURL:
                        saleImageURL.trim(),

                    adminEmail:
                        getAdminEmail(),
                };


                const response =
                    await axios.post(
                        CREATE_SALE_API,
                        payload
                    );


                if (
                    response.data?.success === false
                ) {
                    throw new Error(
                        response.data?.error ||
                        "Unable to create sale."
                    );
                }


                setCreateSuccess(
                    "Sales event created successfully."
                );


                /*
                 * Reload from Cosmos so the Admin page receives the
                 * enriched shopName/shopAddress from getAllSales.
                 */

                await loadData();


                /*
                 * Brief success state before closing.
                 */

                setTimeout(() => {
                    setShowCreateModal(false);
                    setCreateSuccess("");
                }, 700);

            } catch (err) {

                console.error(
                    "Create sale failed:",
                    err
                );


                setCreateError(
                    err.response?.data?.error ||
                    err.response?.data?.message ||
                    err.message ||
                    "Unable to create the sales event."
                );

            } finally {
                setCreating(false);
            }
        };


    /* ==========================================================
       DELETE SALE
       ========================================================== */

    const openDeleteConfirmation = () => {
        setDeleteError("");
        setShowDeleteConfirm(true);
    };


    const closeDeleteConfirmation = () => {

        if (deleting) {
            return;
        }

        setShowDeleteConfirm(false);
        setDeleteError("");
    };


    const handleDeleteSale =
        async () => {

            if (
                !selectedSale?.id ||
                !selectedSale?.shopId
            ) {
                return;
            }


            setDeleting(true);
            setDeleteError("");


            try {

                const response =
                    await axios.post(
                        DELETE_SALE_API,
                        {
                            id:
                                selectedSale.id,

                            shopId:
                                selectedSale.shopId,
                        }
                    );


                if (
                    response.data?.success === false
                ) {
                    throw new Error(
                        response.data?.error ||
                        "Unable to delete sale."
                    );
                }


                /*
                 * Remove locally immediately.
                 */

                setSales((currentSales) =>
                    currentSales.filter(
                        (sale) =>
                            sale.id !==
                            selectedSale.id
                    )
                );


                setShowDeleteConfirm(false);
                setSelectedSale(null);


            } catch (err) {

                console.error(
                    "Delete sale failed:",
                    err
                );


                setDeleteError(
                    err.response?.data?.error ||
                    err.response?.data?.message ||
                    err.message ||
                    "Unable to delete the sales event."
                );

            } finally {
                setDeleting(false);
            }
        };


    /* ==========================================================
       RENDER
       ========================================================== */

    return (
        <div style={styles.page}>

            {/* ======================================================
          HEADER
          ====================================================== */}

            <div style={styles.headerRow}>

                <div>
                    <h1 style={styles.pageTitle}>
                        Sales Events
                    </h1>

                    <p style={styles.pageSubtitle}>
                        View, search and manage Op Shop sales events.
                    </p>
                </div>


                <button
                    type="button"
                    style={styles.createButton}
                    onClick={openCreateModal}
                >
                    + Create Sale
                </button>

            </div>


            {/* ======================================================
          SUMMARY CARDS
          ====================================================== */}

            <div style={styles.summaryGrid}>

                <SummaryCard
                    label="All Sales"
                    count={saleCounts.all}
                    active={
                        statusFilter === "all"
                    }
                    onClick={() =>
                        setStatusFilter("all")
                    }
                />

                <SummaryCard
                    label="Active"
                    count={saleCounts.active}
                    active={
                        statusFilter === "active"
                    }
                    onClick={() =>
                        setStatusFilter("active")
                    }
                />

                <SummaryCard
                    label="Upcoming"
                    count={saleCounts.upcoming}
                    active={
                        statusFilter === "upcoming"
                    }
                    onClick={() =>
                        setStatusFilter("upcoming")
                    }
                />

                <SummaryCard
                    label="Ended"
                    count={saleCounts.ended}
                    active={
                        statusFilter === "ended"
                    }
                    onClick={() =>
                        setStatusFilter("ended")
                    }
                />

            </div>


            {/* ======================================================
          SEARCH
          ====================================================== */}

            <div style={styles.searchSection}>

                <div style={styles.searchWrapper}>

                    <span style={styles.searchIcon}>
                        ⌕
                    </span>


                    <input
                        type="text"
                        value={searchTerm}
                        placeholder="Search sales or Op Shops..."
                        style={styles.searchInput}
                        onChange={(event) => {

                            setSearchTerm(
                                event.target.value
                            );

                            setSelectedShopFilter(null);

                            setShowSearchSuggestions(
                                true
                            );
                        }}
                        onFocus={() =>
                            setShowSearchSuggestions(
                                true
                            )
                        }
                    />


                    {searchTerm && (
                        <button
                            type="button"
                            style={styles.clearSearchButton}
                            onClick={clearSearch}
                        >
                            ×
                        </button>
                    )}


                    {showSearchSuggestions &&
                        searchSuggestions.length > 0 && (

                            <div style={styles.suggestionsBox}>

                                {searchSuggestions.map(
                                    (
                                        suggestion,
                                        index
                                    ) => {

                                        if (
                                            suggestion.type ===
                                            "shop"
                                        ) {

                                            const shop =
                                                suggestion.shop;

                                            return (
                                                <button
                                                    type="button"
                                                    key={
                                                        `shop-${shop.shopId}-${index}`
                                                    }
                                                    style={styles.suggestionItem}
                                                    onMouseDown={(
                                                        event
                                                    ) => {
                                                        event.preventDefault();

                                                        selectSearchSuggestion(
                                                            suggestion
                                                        );
                                                    }}
                                                >
                                                    <div
                                                        style={
                                                            styles.suggestionType
                                                        }
                                                    >
                                                        OP SHOP
                                                    </div>

                                                    <div
                                                        style={
                                                            styles.suggestionTitle
                                                        }
                                                    >
                                                        {shop.name ||
                                                            shop.shopId}
                                                    </div>

                                                    {shop.address && (
                                                        <div
                                                            style={
                                                                styles.suggestionSubtitle
                                                            }
                                                        >
                                                            {shop.address}
                                                        </div>
                                                    )}
                                                </button>
                                            );
                                        }


                                        const sale =
                                            suggestion.sale;

                                        return (
                                            <button
                                                type="button"
                                                key={
                                                    `sale-${sale.id}-${index}`
                                                }
                                                style={styles.suggestionItem}
                                                onMouseDown={(
                                                    event
                                                ) => {
                                                    event.preventDefault();

                                                    selectSearchSuggestion(
                                                        suggestion
                                                    );
                                                }}
                                            >
                                                <div
                                                    style={
                                                        styles.suggestionType
                                                    }
                                                >
                                                    SALE
                                                </div>

                                                <div
                                                    style={
                                                        styles.suggestionTitle
                                                    }
                                                >
                                                    {sale.title}
                                                </div>

                                                <div
                                                    style={
                                                        styles.suggestionSubtitle
                                                    }
                                                >
                                                    {sale.shopName}
                                                </div>
                                            </button>
                                        );
                                    }
                                )}

                            </div>
                        )}

                </div>


                {selectedShopFilter && (
                    <div style={styles.activeFilter}>

                        Showing sales for{" "}

                        <strong>
                            {selectedShopFilter.name ||
                                selectedShopFilter.shopId}
                        </strong>

                        <button
                            type="button"
                            style={
                                styles.removeFilterButton
                            }
                            onClick={clearSearch}
                        >
                            ×
                        </button>

                    </div>
                )}

            </div>


            {/* ======================================================
          RESULTS HEADER
          ====================================================== */}

            <div style={styles.resultsHeader}>

                <div style={styles.resultsCount}>
                    {filteredSales.length}{" "}
                    {filteredSales.length === 1
                        ? "sales event"
                        : "sales events"}
                </div>

                <button
                    type="button"
                    style={styles.refreshButton}
                    onClick={loadData}
                    disabled={loading}
                >
                    {loading
                        ? "Refreshing..."
                        : "Refresh"}
                </button>

            </div>


            {/* ======================================================
          ERROR
          ====================================================== */}

            {error && (
                <div style={styles.errorBox}>
                    {error}
                </div>
            )}


            {/* ======================================================
          LOADING
          ====================================================== */}

            {loading ? (

                <div style={styles.emptyState}>
                    <div style={styles.emptyTitle}>
                        Loading sales...
                    </div>
                </div>

            ) : filteredSales.length === 0 ? (

                <div style={styles.emptyState}>

                    <div style={styles.emptyTitle}>
                        No sales events found
                    </div>

                    <div style={styles.emptyText}>
                        Try changing your search or status filter.
                    </div>

                </div>

            ) : (

                /* ====================================================
                   SALES LIST
                   ==================================================== */

                <div style={styles.salesList}>

                    {filteredSales.map((sale) => {

                        const status =
                            getSaleStatus(sale);

                        return (
                            <button
                                type="button"
                                key={sale.id}
                                style={styles.saleCard}
                                onClick={() => {
                                    setDeleteError("");
                                    setSelectedSale(sale);
                                }}
                            >

                                <div style={styles.saleCardMain}>

                                    <div style={styles.saleCardTopRow}>

                                        <StatusBadge
                                            status={status}
                                        />

                                        <span
                                            style={
                                                styles.createdDate
                                            }
                                        >
                                            Created{" "}
                                            {formatShortDate(
                                                sale.createdAt
                                            )}
                                        </span>

                                    </div>


                                    <div style={styles.saleTitle}>
                                        {sale.title ||
                                            "Untitled Sale"}
                                    </div>


                                    <div style={styles.shopName}>
                                        {sale.shopName ||
                                            sale.shopId}
                                    </div>


                                    {sale.shopAddress && (
                                        <div
                                            style={
                                                styles.shopAddress
                                            }
                                        >
                                            {sale.shopAddress}
                                        </div>
                                    )}


                                    {sale.description && (
                                        <div
                                            style={
                                                styles.saleDescription
                                            }
                                        >
                                            {sale.description}
                                        </div>
                                    )}


                                    <div style={styles.dateRange}>

                                        <span>
                                            <strong>
                                                Starts:
                                            </strong>{" "}
                                            {formatDate(
                                                sale.startDate
                                            )}
                                        </span>

                                        <span
                                            style={
                                                styles.dateDivider
                                            }
                                        >
                                            •
                                        </span>

                                        <span>
                                            <strong>
                                                Ends:
                                            </strong>{" "}
                                            {formatDate(
                                                sale.endDate
                                            )}
                                        </span>

                                    </div>

                                </div>


                                <div style={styles.cardArrow}>
                                    ›
                                </div>

                            </button>
                        );
                    })}

                </div>
            )}


            {/* ======================================================
          SALE DETAILS MODAL
          ====================================================== */}

            {selectedSale && (

                <ModalOverlay>

                    <div style={styles.modal}>

                        <button
                            type="button"
                            style={styles.modalClose}
                            onClick={() => {
                                setSelectedSale(null);
                                setDeleteError("");
                            }}
                        >
                            ×
                        </button>


                        <div style={styles.modalEyebrow}>
                            SALES EVENT
                        </div>


                        <div style={styles.modalTitleRow}>

                            <h2 style={styles.modalTitle}>
                                {selectedSale.title ||
                                    "Untitled Sale"}
                            </h2>

                            <StatusBadge
                                status={getSaleStatus(
                                    selectedSale
                                )}
                            />

                        </div>


                        {selectedSale.description && (
                            <div
                                style={
                                    styles.modalDescription
                                }
                            >
                                {selectedSale.description}
                            </div>
                        )}


                        {selectedSale.imageURL && (

                            <div style={styles.saleImageWrapper}>
                                <img
                                    src={selectedSale.imageURL}
                                    alt={
                                        selectedSale.title ||
                                        "Sale"
                                    }
                                    style={styles.saleImage}
                                />
                            </div>

                        )}


                        <div style={styles.detailGrid}>

                            <DetailBlock
                                label="OP SHOP"
                                value={
                                    selectedSale.shopName ||
                                    selectedSale.shopId
                                }
                            />

                            <DetailBlock
                                label="ADDRESS"
                                value={
                                    selectedSale.shopAddress ||
                                    "Address not available"
                                }
                            />

                            <DetailBlock
                                label="STARTS"
                                value={formatDateTime(
                                    selectedSale.startDate
                                )}
                            />

                            <DetailBlock
                                label="ENDS"
                                value={formatDateTime(
                                    selectedSale.endDate
                                )}
                            />

                            <DetailBlock
                                label="CREATED BY"
                                value={
                                    selectedSale.createdBy ||
                                    "Not recorded"
                                }
                            />

                            <DetailBlock
                                label="ACCESS"
                                value={
                                    selectedSale.createdByAccessType ===
                                        "admin"
                                        ? "OPPIES Admin"
                                        : selectedSale.createdByAccessType
                                            ? capitalize(
                                                selectedSale.createdByAccessType
                                            )
                                            : "Not recorded"
                                }
                            />

                            <DetailBlock
                                label="CREATED"
                                value={formatDateTime(
                                    selectedSale.createdAt
                                )}
                            />

                            <DetailBlock
                                label="SHOP ID"
                                value={
                                    selectedSale.shopId ||
                                    "Not available"
                                }
                            />

                        </div>


                        <div style={styles.modalActions}>

                            <button
                                type="button"
                                style={styles.cancelButton}
                                onClick={() =>
                                    setSelectedSale(null)
                                }
                            >
                                Cancel
                            </button>

                            <button
                                type="button"
                                style={styles.deleteButton}
                                onClick={
                                    openDeleteConfirmation
                                }
                            >
                                Delete Sale
                            </button>

                        </div>

                    </div>

                </ModalOverlay>
            )}


            {/* ======================================================
          DELETE CONFIRMATION MODAL
          ====================================================== */}

            {selectedSale &&
                showDeleteConfirm && (

                    <ModalOverlay
                        higher
                    >

                        <div
                            style={
                                styles.confirmModal
                            }
                        >

                            <div
                                style={
                                    styles.warningIcon
                                }
                            >
                                !
                            </div>


                            <h2
                                style={
                                    styles.confirmTitle
                                }
                            >
                                Delete this sale?
                            </h2>


                            <p
                                style={
                                    styles.confirmText
                                }
                            >
                                Are you sure you want to
                                permanently delete{" "}

                                <strong>
                                    “{selectedSale.title}”
                                </strong>

                                {" "}from{" "}

                                <strong>
                                    {selectedSale.shopName ||
                                        selectedSale.shopId}
                                </strong>
                                ?
                            </p>


                            <p
                                style={
                                    styles.warningText
                                }
                            >
                                This action cannot be undone.
                            </p>


                            {deleteError && (
                                <div
                                    style={
                                        styles.modalError
                                    }
                                >
                                    {deleteError}
                                </div>
                            )}


                            <div
                                style={
                                    styles.confirmActions
                                }
                            >

                                <button
                                    type="button"
                                    style={
                                        styles.cancelButton
                                    }
                                    onClick={
                                        closeDeleteConfirmation
                                    }
                                    disabled={deleting}
                                >
                                    Cancel
                                </button>


                                <button
                                    type="button"
                                    style={
                                        styles.deleteButton
                                    }
                                    onClick={
                                        handleDeleteSale
                                    }
                                    disabled={deleting}
                                >
                                    {deleting
                                        ? "Deleting..."
                                        : "Delete Sale"}
                                </button>

                            </div>

                        </div>

                    </ModalOverlay>
                )}


            {/* ======================================================
          CREATE SALE MODAL
          ====================================================== */}

            {showCreateModal && (

                <ModalOverlay>

                    <div
                        style={
                            styles.createModal
                        }
                    >

                        <button
                            type="button"
                            style={styles.modalClose}
                            onClick={closeCreateModal}
                            disabled={creating}
                        >
                            ×
                        </button>


                        <div style={styles.modalEyebrow}>
                            OPPIES ADMIN
                        </div>


                        <h2 style={styles.modalTitle}>
                            Create Sales Event
                        </h2>


                        <p style={styles.createIntro}>
                            Create a sales event on behalf of an Op Shop.
                        </p>


                        {/* SHOP */}

                        <div style={styles.formGroup}>

                            <label style={styles.label}>
                                Op Shop *
                            </label>


                            <div
                                style={
                                    styles.shopSearchWrapper
                                }
                            >

                                <input
                                    type="text"
                                    value={shopSearch}
                                    placeholder="Search by shop name or address..."
                                    style={styles.input}
                                    onChange={(event) => {

                                        setShopSearch(
                                            event.target.value
                                        );

                                        setCreateShop(null);

                                        setShowShopSuggestions(
                                            true
                                        );
                                    }}
                                    onFocus={() =>
                                        setShowShopSuggestions(
                                            true
                                        )
                                    }
                                />


                                {showShopSuggestions &&
                                    createShopSuggestions.length >
                                    0 && (

                                        <div
                                            style={
                                                styles.createSuggestions
                                            }
                                        >

                                            {createShopSuggestions.map(
                                                (shop) => (

                                                    <button
                                                        type="button"
                                                        key={
                                                            shop.shopId
                                                        }
                                                        style={
                                                            styles.shopSuggestion
                                                        }
                                                        onMouseDown={(
                                                            event
                                                        ) => {

                                                            event.preventDefault();

                                                            selectCreateShop(
                                                                shop
                                                            );
                                                        }}
                                                    >

                                                        <div
                                                            style={
                                                                styles.suggestionTitle
                                                            }
                                                        >
                                                            {shop.name ||
                                                                shop.shopId}
                                                        </div>

                                                        {shop.address && (
                                                            <div
                                                                style={
                                                                    styles.suggestionSubtitle
                                                                }
                                                            >
                                                                {shop.address}
                                                            </div>
                                                        )}

                                                    </button>

                                                )
                                            )}

                                        </div>
                                    )}

                            </div>


                            {createShop && (
                                <div
                                    style={
                                        styles.selectedShopCard
                                    }
                                >

                                    <div
                                        style={
                                            styles.selectedShopLabel
                                        }
                                    >
                                        SELECTED OP SHOP
                                    </div>

                                    <div
                                        style={
                                            styles.selectedShopName
                                        }
                                    >
                                        {createShop.name ||
                                            createShop.shopId}
                                    </div>

                                    {createShop.address && (
                                        <div
                                            style={
                                                styles.selectedShopAddress
                                            }
                                        >
                                            {createShop.address}
                                        </div>
                                    )}

                                </div>
                            )}

                        </div>


                        {/* TITLE */}

                        <div style={styles.formGroup}>

                            <label style={styles.label}>
                                Sale Title *
                            </label>

                            <input
                                type="text"
                                value={saleTitle}
                                placeholder="e.g. $5 Bag Sale"
                                style={styles.input}
                                maxLength={120}
                                onChange={(event) =>
                                    setSaleTitle(
                                        event.target.value
                                    )
                                }
                            />

                        </div>


                        {/* DESCRIPTION */}

                        <div style={styles.formGroup}>

                            <label style={styles.label}>
                                Description
                            </label>

                            <textarea
                                value={saleDescription}
                                placeholder="Describe the sale..."
                                style={styles.textarea}
                                rows={5}
                                onChange={(event) =>
                                    setSaleDescription(
                                        event.target.value
                                    )
                                }
                            />

                        </div>


                        {/* DATES */}

                        <div style={styles.formRow}>

                            <div style={styles.formColumn}>

                                <label style={styles.label}>
                                    Start Date *
                                </label>

                                <input
                                    type="datetime-local"
                                    value={saleStartDate}
                                    style={styles.input}
                                    onChange={(event) =>
                                        setSaleStartDate(
                                            event.target.value
                                        )
                                    }
                                />

                            </div>


                            <div style={styles.formColumn}>

                                <label style={styles.label}>
                                    End Date *
                                </label>

                                <input
                                    type="datetime-local"
                                    value={saleEndDate}
                                    style={styles.input}
                                    onChange={(event) =>
                                        setSaleEndDate(
                                            event.target.value
                                        )
                                    }
                                />

                            </div>

                        </div>


                        {/* IMAGE */}

                        <div style={styles.formGroup}>

                            <label style={styles.label}>
                                Image URL
                            </label>

                            <input
                                type="url"
                                value={saleImageURL}
                                placeholder="Optional"
                                style={styles.input}
                                onChange={(event) =>
                                    setSaleImageURL(
                                        event.target.value
                                    )
                                }
                            />

                            <div style={styles.helperText}>
                                Optional. Leave blank if the sale does not have an image.
                            </div>

                        </div>


                        {saleImageURL && (
                            <div
                                style={
                                    styles.imagePreviewWrapper
                                }
                            >
                                <img
                                    src={saleImageURL}
                                    alt="Sale preview"
                                    style={
                                        styles.imagePreview
                                    }
                                    onError={(event) => {
                                        event.currentTarget.style.display =
                                            "none";
                                    }}
                                />
                            </div>
                        )}


                        {/* ERRORS */}

                        {createError && (
                            <div style={styles.modalError}>
                                {createError}
                            </div>
                        )}


                        {createSuccess && (
                            <div style={styles.successBox}>
                                {createSuccess}
                            </div>
                        )}


                        {/* ACTIONS */}

                        <div style={styles.modalActions}>

                            <button
                                type="button"
                                style={styles.cancelButton}
                                onClick={closeCreateModal}
                                disabled={creating}
                            >
                                Cancel
                            </button>


                            <button
                                type="button"
                                style={styles.primaryButton}
                                onClick={handleCreateSale}
                                disabled={creating}
                            >
                                {creating
                                    ? "Creating..."
                                    : "Create Sale"}
                            </button>

                        </div>

                    </div>

                </ModalOverlay>
            )}

        </div>
    );
}


/* ============================================================
   SMALL COMPONENTS
   ============================================================ */

function SummaryCard({
    label,
    count,
    active,
    onClick,
}) {

    return (
        <button
            type="button"
            onClick={onClick}
            style={{
                ...styles.summaryCard,
                ...(active
                    ? styles.summaryCardActive
                    : {}),
            }}
        >
            <div
                style={{
                    ...styles.summaryCount,
                    ...(active
                        ? styles.summaryCountActive
                        : {}),
                }}
            >
                {count}
            </div>

            <div
                style={{
                    ...styles.summaryLabel,
                    ...(active
                        ? styles.summaryLabelActive
                        : {}),
                }}
            >
                {label}
            </div>
        </button>
    );
}


function StatusBadge({
    status,
}) {

    const badgeStyle = {
        ...styles.statusBadge,
    };


    if (status === "active") {
        Object.assign(
            badgeStyle,
            styles.statusActive
        );
    }


    if (status === "upcoming") {
        Object.assign(
            badgeStyle,
            styles.statusUpcoming
        );
    }


    if (status === "ended") {
        Object.assign(
            badgeStyle,
            styles.statusEnded
        );
    }


    return (
        <span style={badgeStyle}>
            {getStatusLabel(status)}
        </span>
    );
}


function DetailBlock({
    label,
    value,
}) {

    return (
        <div style={styles.detailBlock}>

            <div style={styles.detailLabel}>
                {label}
            </div>

            <div style={styles.detailValue}>
                {value}
            </div>

        </div>
    );
}


function ModalOverlay({
    children,
    higher = false,
}) {

    return (
        <div
            style={{
                ...styles.overlay,
                zIndex:
                    higher
                        ? 2000
                        : 1000,
            }}
        >
            {children}
        </div>
    );
}


function capitalize(value) {

    const text =
        String(value || "");

    if (!text) {
        return "";
    }

    return (
        text.charAt(0).toUpperCase() +
        text.slice(1)
    );
}


/* ============================================================
   STYLES
   ============================================================ */

const styles = {

    page: {
        width: "100%",
        maxWidth: "1400px",
        margin: "0 auto",
        padding: "28px",
        boxSizing: "border-box",
        fontFamily:
            "'Inter', 'Segoe UI', Arial, sans-serif",
        color: "#17372d",
    },


    /* HEADER */

    headerRow: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: "20px",
        flexWrap: "wrap",
        marginBottom: "26px",
    },

    pageTitle: {
        margin: 0,
        fontSize: "32px",
        fontWeight: 800,
        color: "#17372d",
    },

    pageSubtitle: {
        margin: "7px 0 0",
        color: "#6f7e78",
        fontSize: "14px",
    },

    createButton: {
        border: "none",
        borderRadius: "12px",
        background: "#17372d",
        color: "#ffffff",
        padding: "13px 20px",
        fontSize: "14px",
        fontWeight: 700,
        cursor: "pointer",
        boxShadow:
            "0 6px 18px rgba(23,55,45,0.16)",
    },


    /* SUMMARY */

    summaryGrid: {
        display: "grid",
        gridTemplateColumns:
            "repeat(auto-fit, minmax(150px, 1fr))",
        gap: "12px",
        marginBottom: "22px",
    },

    summaryCard: {
        textAlign: "left",
        background: "#ffffff",
        border: "1px solid #e2e8e5",
        borderRadius: "14px",
        padding: "17px 18px",
        cursor: "pointer",
        transition: "all 0.2s ease",
    },

    summaryCardActive: {
        background: "#17372d",
        borderColor: "#17372d",
    },

    summaryCount: {
        fontSize: "25px",
        lineHeight: 1,
        fontWeight: 800,
        color: "#17372d",
    },

    summaryCountActive: {
        color: "#ffffff",
    },

    summaryLabel: {
        marginTop: "8px",
        fontSize: "12px",
        fontWeight: 700,
        color: "#76847f",
        textTransform: "uppercase",
        letterSpacing: "0.7px",
    },

    summaryLabelActive: {
        color: "#dce9e4",
    },


    /* SEARCH */

    searchSection: {
        marginBottom: "20px",
    },

    searchWrapper: {
        position: "relative",
        width: "100%",
    },

    searchIcon: {
        position: "absolute",
        left: "17px",
        top: "50%",
        transform: "translateY(-50%)",
        color: "#73817c",
        fontSize: "23px",
        zIndex: 2,
    },

    searchInput: {
        width: "100%",
        height: "52px",
        boxSizing: "border-box",
        border: "1px solid #dce4e0",
        borderRadius: "14px",
        background: "#ffffff",
        padding: "0 52px 0 48px",
        fontSize: "15px",
        outline: "none",
        color: "#17372d",
    },

    clearSearchButton: {
        position: "absolute",
        right: "14px",
        top: "50%",
        transform: "translateY(-50%)",
        width: "30px",
        height: "30px",
        border: "none",
        borderRadius: "50%",
        background: "#edf1ef",
        color: "#52615c",
        fontSize: "20px",
        cursor: "pointer",
    },

    suggestionsBox: {
        position: "absolute",
        left: 0,
        right: 0,
        top: "58px",
        background: "#ffffff",
        border: "1px solid #dfe6e3",
        borderRadius: "14px",
        boxShadow:
            "0 14px 35px rgba(24,50,42,0.15)",
        overflow: "hidden",
        zIndex: 100,
        maxHeight: "430px",
        overflowY: "auto",
    },

    suggestionItem: {
        width: "100%",
        textAlign: "left",
        border: "none",
        borderBottom:
            "1px solid #edf1ef",
        background: "#ffffff",
        padding: "13px 16px",
        cursor: "pointer",
    },

    suggestionType: {
        fontSize: "9px",
        fontWeight: 800,
        letterSpacing: "1px",
        color: "#779187",
        marginBottom: "3px",
    },

    suggestionTitle: {
        fontSize: "14px",
        fontWeight: 700,
        color: "#17372d",
    },

    suggestionSubtitle: {
        marginTop: "3px",
        fontSize: "12px",
        lineHeight: 1.4,
        color: "#75827d",
    },

    activeFilter: {
        display: "inline-flex",
        alignItems: "center",
        gap: "5px",
        marginTop: "10px",
        background: "#edf5f1",
        borderRadius: "20px",
        padding: "7px 11px",
        fontSize: "12px",
        color: "#315348",
    },

    removeFilterButton: {
        border: "none",
        background: "transparent",
        fontSize: "17px",
        color: "#315348",
        cursor: "pointer",
        padding: "0 2px",
    },


    /* RESULTS */

    resultsHeader: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: "12px",
    },

    resultsCount: {
        fontSize: "13px",
        fontWeight: 700,
        color: "#697771",
    },

    refreshButton: {
        border: "none",
        background: "transparent",
        color: "#315d4d",
        fontSize: "13px",
        fontWeight: 700,
        cursor: "pointer",
    },


    /* SALES LIST */

    salesList: {
        display: "flex",
        flexDirection: "column",
        gap: "11px",
    },

    saleCard: {
        width: "100%",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: "20px",
        textAlign: "left",
        border: "1px solid #e1e7e4",
        borderRadius: "15px",
        background: "#ffffff",
        padding: "18px 20px",
        cursor: "pointer",
        boxShadow:
            "0 3px 12px rgba(26,52,44,0.04)",
    },

    saleCardMain: {
        flex: 1,
        minWidth: 0,
    },

    saleCardTopRow: {
        display: "flex",
        alignItems: "center",
        gap: "10px",
        flexWrap: "wrap",
        marginBottom: "9px",
    },

    createdDate: {
        fontSize: "11px",
        color: "#8a9692",
    },

    saleTitle: {
        fontSize: "18px",
        fontWeight: 800,
        color: "#17372d",
        marginBottom: "4px",
    },

    shopName: {
        fontSize: "14px",
        fontWeight: 700,
        color: "#426357",
    },

    shopAddress: {
        fontSize: "12px",
        color: "#7a8782",
        marginTop: "2px",
    },

    saleDescription: {
        fontSize: "13px",
        lineHeight: 1.55,
        color: "#596963",
        marginTop: "10px",
        maxWidth: "850px",
    },

    dateRange: {
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "8px",
        marginTop: "12px",
        fontSize: "12px",
        color: "#5f6d68",
    },

    dateDivider: {
        color: "#b2bdb9",
    },

    cardArrow: {
        fontSize: "31px",
        color: "#8fa09a",
        fontWeight: 300,
    },


    /* STATUS */

    statusBadge: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "20px",
        padding: "5px 9px",
        fontSize: "9px",
        lineHeight: 1,
        fontWeight: 800,
        letterSpacing: "0.8px",
    },

    statusActive: {
        background: "#e3f4e9",
        color: "#267442",
    },

    statusUpcoming: {
        background: "#e7eef8",
        color: "#345f8e",
    },

    statusEnded: {
        background: "#f0f1f1",
        color: "#747c79",
    },


    /* EMPTY / ERROR */

    emptyState: {
        background: "#ffffff",
        border: "1px solid #e2e8e5",
        borderRadius: "16px",
        padding: "55px 25px",
        textAlign: "center",
    },

    emptyTitle: {
        fontSize: "17px",
        fontWeight: 800,
        color: "#17372d",
    },

    emptyText: {
        marginTop: "7px",
        color: "#7a8782",
        fontSize: "13px",
    },

    errorBox: {
        padding: "13px 15px",
        marginBottom: "15px",
        background: "#fff0f0",
        border: "1px solid #f1caca",
        borderRadius: "10px",
        color: "#a23838",
        fontSize: "13px",
    },


    /* OVERLAY */

    overlay: {
        position: "fixed",
        inset: 0,
        background:
            "rgba(13, 29, 24, 0.58)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: "20px",
        boxSizing: "border-box",
        overflowY: "auto",
    },


    /* DETAILS MODAL */

    modal: {
        position: "relative",
        width: "100%",
        maxWidth: "720px",
        maxHeight: "90vh",
        overflowY: "auto",
        background: "#fbfaf6",
        borderRadius: "22px",
        padding: "30px",
        boxSizing: "border-box",
        boxShadow:
            "0 25px 70px rgba(0,0,0,0.24)",
    },

    createModal: {
        position: "relative",
        width: "100%",
        maxWidth: "760px",
        maxHeight: "92vh",
        overflowY: "auto",
        background: "#fbfaf6",
        borderRadius: "22px",
        padding: "30px",
        boxSizing: "border-box",
        boxShadow:
            "0 25px 70px rgba(0,0,0,0.24)",
    },

    modalClose: {
        position: "absolute",
        right: "18px",
        top: "16px",
        width: "34px",
        height: "34px",
        border: "none",
        borderRadius: "50%",
        background: "#edf0ed",
        color: "#46564f",
        fontSize: "22px",
        cursor: "pointer",
    },

    modalEyebrow: {
        fontSize: "10px",
        fontWeight: 800,
        letterSpacing: "1.4px",
        color: "#779087",
        marginBottom: "8px",
    },

    modalTitleRow: {
        display: "flex",
        alignItems: "center",
        gap: "12px",
        flexWrap: "wrap",
        paddingRight: "35px",
    },

    modalTitle: {
        margin: 0,
        fontSize: "26px",
        lineHeight: 1.2,
        color: "#17372d",
        fontWeight: 800,
    },

    modalDescription: {
        marginTop: "17px",
        fontSize: "14px",
        lineHeight: 1.65,
        color: "#52635c",
        whiteSpace: "pre-wrap",
    },

    saleImageWrapper: {
        marginTop: "20px",
        width: "100%",
        borderRadius: "14px",
        overflow: "hidden",
        background: "#eef1ef",
    },

    saleImage: {
        width: "100%",
        maxHeight: "320px",
        display: "block",
        objectFit: "cover",
    },


    /* DETAILS */

    detailGrid: {
        display: "grid",
        gridTemplateColumns:
            "repeat(auto-fit, minmax(230px, 1fr))",
        gap: "11px",
        marginTop: "22px",
    },

    detailBlock: {
        background: "#ffffff",
        border: "1px solid #e5e9e6",
        borderRadius: "12px",
        padding: "13px 14px",
        minWidth: 0,
    },

    detailLabel: {
        fontSize: "9px",
        fontWeight: 800,
        letterSpacing: "1px",
        color: "#87938f",
        marginBottom: "5px",
    },

    detailValue: {
        fontSize: "13px",
        lineHeight: 1.5,
        fontWeight: 600,
        color: "#29483e",
        wordBreak: "break-word",
    },


    /* MODAL ACTIONS */

    modalActions: {
        display: "flex",
        justifyContent: "flex-end",
        gap: "10px",
        flexWrap: "wrap",
        marginTop: "25px",
        paddingTop: "18px",
        borderTop: "1px solid #e2e7e4",
    },

    cancelButton: {
        border: "1px solid #d6ddda",
        background: "#ffffff",
        color: "#44554e",
        borderRadius: "11px",
        padding: "11px 18px",
        fontSize: "13px",
        fontWeight: 700,
        cursor: "pointer",
    },

    primaryButton: {
        border: "none",
        background: "#17372d",
        color: "#ffffff",
        borderRadius: "11px",
        padding: "11px 20px",
        fontSize: "13px",
        fontWeight: 700,
        cursor: "pointer",
    },

    deleteButton: {
        border: "none",
        background: "#a84242",
        color: "#ffffff",
        borderRadius: "11px",
        padding: "11px 18px",
        fontSize: "13px",
        fontWeight: 700,
        cursor: "pointer",
    },


    /* DELETE CONFIRM */

    confirmModal: {
        width: "100%",
        maxWidth: "460px",
        background: "#fbfaf6",
        borderRadius: "20px",
        padding: "30px",
        textAlign: "center",
        boxSizing: "border-box",
        boxShadow:
            "0 25px 70px rgba(0,0,0,0.28)",
    },

    warningIcon: {
        width: "46px",
        height: "46px",
        margin: "0 auto 15px",
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#f7e4e4",
        color: "#a84242",
        fontWeight: 900,
        fontSize: "22px",
    },

    confirmTitle: {
        margin: "0 0 12px",
        color: "#17372d",
        fontSize: "22px",
    },

    confirmText: {
        margin: 0,
        fontSize: "14px",
        lineHeight: 1.6,
        color: "#586861",
    },

    warningText: {
        margin: "12px 0 0",
        fontSize: "12px",
        fontWeight: 700,
        color: "#a84242",
    },

    confirmActions: {
        display: "flex",
        justifyContent: "center",
        gap: "10px",
        marginTop: "24px",
    },


    /* CREATE */

    createIntro: {
        margin: "9px 0 24px",
        fontSize: "13px",
        color: "#71807a",
    },

    formGroup: {
        marginBottom: "17px",
    },

    formRow: {
        display: "grid",
        gridTemplateColumns:
            "repeat(auto-fit, minmax(220px, 1fr))",
        gap: "14px",
        marginBottom: "17px",
    },

    formColumn: {
        minWidth: 0,
    },

    label: {
        display: "block",
        marginBottom: "6px",
        color: "#315046",
        fontSize: "12px",
        fontWeight: 700,
    },

    input: {
        width: "100%",
        minHeight: "45px",
        boxSizing: "border-box",
        border: "1px solid #d8e0dc",
        borderRadius: "10px",
        background: "#ffffff",
        padding: "10px 12px",
        fontSize: "14px",
        color: "#17372d",
        outline: "none",
    },

    textarea: {
        width: "100%",
        boxSizing: "border-box",
        border: "1px solid #d8e0dc",
        borderRadius: "10px",
        background: "#ffffff",
        padding: "11px 12px",
        fontFamily: "inherit",
        fontSize: "14px",
        lineHeight: 1.5,
        color: "#17372d",
        outline: "none",
        resize: "vertical",
    },

    helperText: {
        marginTop: "5px",
        color: "#89948f",
        fontSize: "11px",
    },


    /* CREATE SHOP SEARCH */

    shopSearchWrapper: {
        position: "relative",
    },

    createSuggestions: {
        position: "absolute",
        left: 0,
        right: 0,
        top: "51px",
        background: "#ffffff",
        border: "1px solid #dfe5e2",
        borderRadius: "11px",
        overflow: "hidden",
        boxShadow:
            "0 12px 28px rgba(24,50,42,0.16)",
        zIndex: 150,
        maxHeight: "300px",
        overflowY: "auto",
    },

    shopSuggestion: {
        display: "block",
        width: "100%",
        textAlign: "left",
        border: "none",
        borderBottom:
            "1px solid #edf1ef",
        background: "#ffffff",
        padding: "11px 13px",
        cursor: "pointer",
    },

    selectedShopCard: {
        marginTop: "9px",
        background: "#edf5f1",
        border: "1px solid #d7e6df",
        borderRadius: "10px",
        padding: "11px 13px",
    },

    selectedShopLabel: {
        fontSize: "8px",
        letterSpacing: "1px",
        fontWeight: 800,
        color: "#779087",
    },

    selectedShopName: {
        marginTop: "3px",
        fontSize: "13px",
        fontWeight: 800,
        color: "#264b3e",
    },

    selectedShopAddress: {
        marginTop: "2px",
        fontSize: "11px",
        color: "#6d7e77",
    },


    /* IMAGE PREVIEW */

    imagePreviewWrapper: {
        marginBottom: "17px",
        borderRadius: "12px",
        overflow: "hidden",
        background: "#eef1ef",
    },

    imagePreview: {
        display: "block",
        width: "100%",
        maxHeight: "250px",
        objectFit: "cover",
    },


    /* CREATE / DELETE MESSAGES */

    modalError: {
        marginTop: "15px",
        padding: "11px 13px",
        borderRadius: "9px",
        background: "#fff0f0",
        border: "1px solid #efcccc",
        color: "#9e3737",
        fontSize: "12px",
    },

    successBox: {
        marginTop: "15px",
        padding: "11px 13px",
        borderRadius: "9px",
        background: "#e9f6ed",
        border: "1px solid #cde7d5",
        color: "#2e7044",
        fontSize: "12px",
        fontWeight: 700,
    },
};